/**
 * @module app.track.poi
 */
import appBase from '../index.js';
import olFormatGeoJSON from 'ol/format/GeoJSON.js';
import olStyleIcon from 'ol/style/Icon.js';
import olStyleStyle from 'ol/style/Style.js';
import ngeoInteractionMobileDraw from 'ngeo/interaction/MobileDraw.js';
import {MAIN_MENU_EVENT_NAME, TRACK_POI_EVENT_NAME} from "../constants";
import {makeIcon, SIZE as IMG_SIZE} from "./poiicon";


class Controller {
  /**
   * @param {angular.Scope} $scope Angular scope.
   * @param {angular.$http} $http Angular Http service.
   * @param {string} apiUrl API url prefix.
   * @param {string} s3Url S3 url prefix.
   * @param {angular.$timeout} $timeout Angular timeout service.
   * @param {ui.router.$stateParams} $stateParams
   * @param {angularGettext.Catalog} gettextCatalog
   * @param {!app.query.state.Service} appQueryStateService
   * @param {app.MapService} appMapService
   * @param {app.AuthenticationService} appAuthenticationService
   * @param {!app.CesiumLoader} appCesiumLoader
   * @param {{photos: Array<Object>, rotate_hint: boolean}} appCarouselPhotos
   * @param {app.waiting} appWaiting,
   * @param {ngeo.statemanager.Location} ngeoLocation Location service from NGEO.
   * @ngInject
   */
  constructor($scope, $http, apiUrl, s3Url, $timeout, $stateParams, gettextCatalog, appQueryStateService, appMapService,
    appAuthenticationService, appCesiumLoader, appCarouselPhotos, appWaiting, ngeoLocation) {
    // TODO refactor modals and remove this
    $scope.$root['modalsTracksPoiCtrl'] = this;

    /**
     * @private
     */
    this.scope_ = $scope;

    /**
     * @private
     */
    this.apiUrl_ = apiUrl;

    /**
     * @export
     */
    this.s3Url = s3Url;

    /**
     * @private
     */
    this.timeout_ = $timeout;

    /**
     * @private
     */
    this.stateParams_ = $stateParams;


    this.gettextCatalog = gettextCatalog;

    /**
     * @private
     */
    this.queryStateService_ = appQueryStateService;

    /**
     * @private
     */
    this.authentication_ = appAuthenticationService;

    /**
     * @private
     */
    this.cesium_ = appCesiumLoader;

    /**
     * @private
     */
    this.appCarouselPhotos_ = appCarouselPhotos;

    /**
     * @private
     */
    this.appWaiting_ = appWaiting;

    /**
     * @type {ol.Map}
     * @export
     */
    this.map = appMapService.map;

    /**
     * @export
     * @type {function(appx.PoiChangeEvent)}
     */
    this.onPoiChanged;

    /**
     * @type {ol.Feature}
     * @private
     */
    this.activePOI_ = null;

    /**
     * @type {appx.TrackPOIProperties}
     * @export
     */
    this.activePOIProperties = this.createEmptyPOIProperties_();

    /**
     * @type {boolean}
     * @export
     */
    this.isEditing = false;

    /**
     * @type {angular.$http}
     * @private
     */
    this.http_ = $http;

    /**
     * @type {ol.format.GeoJSON}
     * @private
     */
    this.geojsonFormat_ = new olFormatGeoJSON();

    /**
     * @type {ngeo.interaction.MobileDraw}
     * @export
     */
    this.addPOIInteraction;

    /**
     * @type {ngeo.interaction.MobileDraw}
     * @export
     */
    this.movePOIInteraction;

    /**
     * @type {Object}
     * @export
     */
    this.poiPlusPosition = {};

    /**
     * @type {app.track.TracksController}
     * @export
     */
    this.tracksCtrl;

    this.scope_.$on('submenuChange', (event, data) => {
      data = /** @type {appx.SubmenuChangeEvent} */ (data);
      if (!data.open && data.dom) {
        this.cancelEditActivePOI();
      }
    });

    this.scope_.$on(TRACK_POI_EVENT_NAME.FINISH_DRAWING, () => {
      this.scope_.$root['mainCtrl'].showMenu('mytracks', true);

      if (this.addPOIInteraction) {
        this.addPoi_(this.addPOIInteraction.getFeature());
      }
      if (this.movePOIInteraction) {
        this.movePoi_(this.movePOIInteraction.getFeature());
      }
    });
  }

  /**
   * @export
   * @return {ol.Feature}
   */
  get activePOI() {
    return this.activePOI_;
  }

  /**
   * @export
   * @param {ol.Feature} activePOI
   */
  set activePOI(activePOI) {
    this.activePOI_ = activePOI;
    if (this.onPoiChanged) {
      this.onPoiChanged({activePOI: this.activePOI});
    }
  }

  $onInit() {
    const activePOI = this.stateParams_['activePOI'];
    if (activePOI) {
      this.populateActivePOI_(activePOI);
    } else {
      this.toggleAddPOI();
    }
  }

  $onDestroy() {
    this.deactivatePOI();
    this.removePoiInteractions_();
  }

  /**
   * @param {ol.Feature} feature
   * @private
   */
  populateActivePOI_(feature) {
    this.activePOI = feature;
    // Copy of the properties?
    this.activePOIProperties = /** @type {appx.TrackPOIProperties} */ (
      feature.getProperties()
    );
  }


  /**
   * Hack to avoid creating a valid empty properties.
   * @return {appx.TrackPOIProperties}
   * @private
   */
  createEmptyPOIProperties_() {
    return /** @type {appx.TrackPOIProperties} */ ({});
  }

  /**
   * @export
   */
  setPlusPosition(image) {
    if (image.width() === 0) {
      window.requestAnimationFrame(() => {
        this.setPlusPosition(image);
      });
    } else {
      this.poiPlusPosition = {
        position: 'absolute',
        left: image.width(),
        top: image.height()
      };
      this.scope_.$apply();
    }
  }

  /**
   * @private
   */
  addPoiInteractions_() {
    this.scope_.$root.$broadcast(TRACK_POI_EVENT_NAME.SET_EDITING_STATE, true);
    this.deactivatePOI();
    this.scope_.$root.$broadcast(MAIN_MENU_EVENT_NAME.COLLAPSE);
    const {color} = this.tracksCtrl.appTrackService.selectedTrack.properties;
    if (this.addPOIInteraction) {
      this.map.removeInteraction(this.addPOIInteraction);
    }
    this.addPOIInteraction = new ngeoInteractionMobileDraw({
      style: new olStyleStyle({
        image: new olStyleIcon({
          imgSize: IMG_SIZE,
          src: makeIcon(color),
          anchor: [0.5, 1]
        })
      }),
      type: 'Point'
    });

    this.map.addInteraction(this.addPOIInteraction);
  }

  /**
   * @private
   * @param {ol.Feature} feature
   */
  addPoi_(feature) {
    feature.setStyle(this.tracksCtrl.trackPOIStyleFunction);
    let trackProp = 'recordedtrack';
    if (!this.tracksCtrl.appTrackService.selectedTrack.recorded) {
      trackProp = 'newtrack';
    }
    feature.setProperties(Object.assign({
      'addpoi': 'unsaved',
      'title': '',
      [trackProp]: this.tracksCtrl.selectedTrackId,
      'sort': undefined,
      'type': 'track_poi',
      'photo': '',
      'dist': 0
    }, this.activePOIProperties));

    feature.setId(1); // previous website sends a create with id = 1
    this.activePOI = feature;
    this.activePOIProperties = /** @type {appx.TrackPOIProperties} */ (
      feature.getProperties()
    );

    this.addPOIInteraction.finishDrawing();
  }

  /**
   * @private
   * @param {ol.Feature} feature
   */
  movePoi_(feature) {
    feature.setStyle(function() {
      return null;
    });
    const geometry = feature.getGeometry();
    this.activePOI.setGeometry(geometry);
    this.removePoiInteractions_();
  }

  /**
   * @private
   */
  removePoiInteractions_() {
    if (this.addPOIInteraction) {
      this.map.removeInteraction(this.addPOIInteraction);
      this.addPOIInteraction = null;
    }
    if (this.movePOIInteraction) {
      this.map.removeInteraction(this.movePOIInteraction);
      this.movePOIInteraction = null;
    }
    // Hack to prevent info menu to pop up
    this.timeout_(() => {
      this.queryStateService_.removeQueryBlocker('tracksPoi');
    }, 1000);
  }

  /**
   * @export
   */
  toggleAddPOI() {
    this.activePOI = null;
    this.activePOIProperties = this.createEmptyPOIProperties_();
    const trackPoisSource = this.tracksCtrl.selectedTrackPoisLayer.getSource();

    if (trackPoisSource.getFeatures().length >= 99) {
      bootbox.alert({
        title: this.gettextCatalog.getString('Warning'),
        message: this.gettextCatalog.getString('You have reached the max numbers of POI (99)')
      });
      return;
    }

    this.authentication_.isUserLoggedIn((loggedIn, subscriptionOk) => {
      if (!loggedIn || !subscriptionOk) {
        return;
      }

      this.isEditing = !this.isEditing;

      if (this.isEditing) {
        this.queryStateService_.addQueryBlocker('tracksPoi');
        this.addPoiInteractions_();
      } else {
        this.removePoiInteractions_();
      }
    });
  }


  /**
   * @export
   */
  cancelEditActivePOI() {
    if (this.activePOI) {
      this.scope_.$root.$broadcast(TRACK_POI_EVENT_NAME.SET_EDITING_STATE, false);
      // Ugly trick: old geometry is in the activePOIProperties hash
      const newGeometry = this.activePOI.getGeometry();
      const oldGeometry = this.activePOIProperties.geometry;
      if (newGeometry !== oldGeometry) {
        this.activePOI.setGeometry(oldGeometry);
      }
      this.activePOIProperties = this.createEmptyPOIProperties_();
      if (this.activePOI.getId() === 1) {
        // Remove from the UI if new POI
        const source = this.tracksCtrl.selectedTrackPoisLayer.getSource();
        const exists = source.getFeatureById(1);
        if (exists) {
          source.removeFeature(this.activePOI);
        }
        this.tracksCtrl.refreshPOIs();
      }
    }
    this.tracksCtrl.routingState.go('track-facts');
  }


  /**
   * @export
   */
  moveActivePOI() {
    if (!this.activePOI) {
      // actually we have no active POI so we will enable the add poi interactions instead
      // this occurs when the menu button is clicked while the POI was not yet added.
      this.addPoiInteractions_();
      return;
    }

    if (!this.movePOIInteraction) {
      // recenter map on poi
      const center = /** @type {ol.geom.Point} */ (this.activePOI.getGeometry()).getCoordinates();
      this.map.getView().setCenter(center);

      this.removePoiInteractions_();
      this.scope_.$root.$broadcast(TRACK_POI_EVENT_NAME.SET_EDITING_STATE, true);
      this.queryStateService_.addQueryBlocker('tracksPoi');
      this.scope_.$root.$broadcast(MAIN_MENU_EVENT_NAME.COLLAPSE);
      const {color} = this.tracksCtrl.appTrackService.selectedTrack.properties;
      this.movePOIInteraction = new ngeoInteractionMobileDraw({
        style: new olStyleStyle({
          image: new olStyleIcon({
            imgSize: IMG_SIZE,
            src: makeIcon(color),
            anchor: [0.5, 1]
          })
        }),
        type: 'Point'
      });
      this.map.addInteraction(this.movePOIInteraction);
    } else {
      this.scope_.$root.$broadcast(MAIN_MENU_EVENT_NAME.COLLAPSE);
    }
  }


  /**
   * @export
   */
  saveActivePOI() {
    const title = this.activePOIProperties.title;
    if (!title || !this.activePOI) {
      // otherwise an error is displayed by the UI (handled by the UI)
      return;
    }

    const feature = this.activePOI;
    const newGeometry = feature.getGeometry();
    console.assert(newGeometry);
    // Ugly trick: dirty geometry is in the current feature
    this.activePOIProperties.geometry = newGeometry;
    feature.setProperties(this.activePOIProperties);
    this.isEditing = false;

    if (!this.activePOIProperties.sort) {
      this.createPOI();
    } else {
      this.updatePOI();
    }
    this.scope_.$root.$broadcast(TRACK_POI_EVENT_NAME.SET_EDITING_STATE, false);
    this.removePoiInteractions_();
  }


  /**
   * @export
   */
  removeActivePOI() {
    const source = this.tracksCtrl.selectedTrackPoisLayer.getSource();

    const id = this.activePOI.getId();
    this.activePOIProperties = this.createEmptyPOIProperties_();

    const url = this.apiUrl_ + '4/pois/' + id;
    const request = this.http_.delete(url);
    request.then(() => {
      // Removing from the UI
      source.removeFeature(this.activePOI);
      this.activePOI = null;

      this.tracksCtrl.refreshPOIs();
      this.tracksCtrl.routingState.go('track-facts');
    }).catch(err => {
      console.error(err);
      bootbox.alert({
        title: this.gettextCatalog.getString('An error occurred'),
        message: this.gettextCatalog.getString('Error when deleting poi')
      });
      this.tracksCtrl.routingState.go('track-facts');
    });
  }


  /**
   * @export
   */
  deactivatePOI() {
    this.activePOI = null;
  }


  /**
   * @param {FileList} files Browser files
   * @export
   */
  uploadPoiImage(files) {
    if (files && files.length) {
      const file = files[0];

      const showError = () => {
        const msg = this.gettextCatalog.getString(
          'The photo has to be in JPG, PNG or HEIC format and has a maximum size of 10 MB');
        bootbox.alert({
          title: this.gettextCatalog.getString('Photo upload not possible'),
          message: msg
        });
      };

      if (!['image/jpeg', 'image/png', 'image/heif'].includes(file.type) || file.size >= 10485760) {
        showError();
      } else {
        this.appWaiting_.increment();
        const formData = new FormData();
        formData.append('file', file);

        const request = this.http_.post(this.apiUrl_ + '6/pois/addimage', formData, {
          headers: {'Content-Type': undefined}
        });

        request.then(function(response) {
          this.activePOIProperties.photo = response.data.filename;
          this.activePOIProperties.tempPhoto = true;
        }.bind(this), showError);

        request.finally(function() {
          this.appWaiting_.decrement();
        }.bind(this)).catch(err => console.error(err));

      }
    }
  }


  /**
   * Create a POI on the server
   * @export
   */
  createPOI() {
    const objects = this.geojsonFormat_.writeFeaturesObject([this.activePOI]);

    const request = this.http_.post(this.apiUrl_ + '4/pois', objects);
    request.then(function(response) {
      const feature = this.geojsonFormat_.readFeature(response.data['features'][0]);
      this.refreshPOIs_(feature.getId());
    }.bind(this)).catch(err => {
      console.error(err);
      bootbox.alert({
        title: this.gettextCatalog.getString('An error occurred'),
        message: this.gettextCatalog.getString('Error when saving poi')
      });
      this.cancelEditActivePOI();
    });

  }


  /**
   * Refresh POIs
   * @private
   * @param {number|string|undefined} id Feature id of the POI.
   */
  refreshPOIs_(id) {
    this.activePOIProperties.tempPhoto = false;
    const promise = this.tracksCtrl.refreshPOIs();
    promise.then(function(features) {
      features = /** @type {Array.<ol.Feature>} */ (features);
      // Refresh active POI reference to newly created feature
      for (let i = 0; i < features.length; ++i) {
        if (features[i].getId() === id) {
          this.populateActivePOI_(features[i]);
          return;
        }
      }
    }.bind(this));
  }


  /**
   * Update a POI on the server
   * @export
   */
  updatePOI() {
    const object = this.geojsonFormat_.writeFeatureObject(this.activePOI);

    const id = this.activePOI.getId();
    const url = this.apiUrl_ + '4/pois/' + id;
    const request = this.http_.put(url, object);
    request.then(function() {
      this.refreshPOIs_(id);
    }.bind(this)).catch(err => {
      console.error(err);
      bootbox.alert({
        title: this.gettextCatalog.getString('An error occurred'),
        message: this.gettextCatalog.getString('Error when updating poi')
      });
      this.cancelEditActivePOI();
    });
  }


  /**
   * @export
   */
  deletePOIDialog() {
    bootbox.addLocale('delete_poi', {
      OK: 'Ok',
      CANCEL: this.gettextCatalog.getString('Cancel deletion'),
      CONFIRM: this.gettextCatalog.getString('Delete')
    });
    bootbox.setLocale('delete_poi');
    bootbox.confirm({
      title: this.gettextCatalog.getString('Delete POI'),
      message: this.gettextCatalog.getString('Are you sure you want to delete the POI?'),
      callback: (result) => {
        if (result) {
          this.removeActivePOI();
        }
      }
    });
  }


  /**
   * @export
   */
  editPoi() {
    this.authentication_.isUserLoggedIn((loggedIn, subscriptionOk) => {
      if (loggedIn && subscriptionOk) {
        this.isEditing = true;
      }
    });
  }

  /**
   * @export
   */
  showModal() {
    this.appCarouselPhotos_.photos = [{
      photo: `${this.s3Url}valid/${this.activePOIProperties.photo}_big.jpg`
    }];
    this.appCarouselPhotos_.rotate_hint = false;
  }

  /**
   * @export
   */
  not3d() {
    return this.cesium_.not3d();
  }

  /**
   * @export
   * @return {boolean}
   */
  olcsEnabled() {
    return this.cesium_.olcsEnabled;
  }

  /**
   * @export
   * @return {boolean}
   */
  loggedIn() {
    return this.authentication_.loggedIn;
  }
}


/**
 * @const {!angular.Component}
 */
const component = {
  bindings: {
    'tracksCtrl': '<',
    'onPoiChanged': '&'
  },
  controller: Controller,
  templateUrl: 'js/app/track/poi/partial.html'
};

export default appBase.component('appTracksPoi', component);
